// React
import PropTypes from "prop-types";

// @mui material components
import {
  Card,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
  Alert,
} from "@mui/material";

// Custom UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { useContext, useEffect, useRef, useState } from "react";
import { useLazyQuery, useMutation, gql } from "@apollo/client";
import { MessageManager } from "context";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "40%", xxl: "30%" },
  overflow: "visible",
};

export default function ModalCambiarMenu({ open, handleClose, refetch, pedido }) {
  const [idMenu, setIdMenu] = useState("-1");
  const [turno, setTurno] = useState("");
  const [menusDisponibles, setMenusDisponibles] = useState([]);
  const { handleSnackbar } = useContext(MessageManager);

  const inputRef = useRef(null);

  const [getProductos, { loading, data }] = useLazyQuery(
    gql`
      query getMenusDelDia($filter: JSON) {
        menusDelDia(filter: $filter) {
          id
          precio
          fechaMenu
          limiteReservas
          cantidadReservas
          producto {
            id
            nombre
            disponibilidad
            tipoProducto {
              id
              tipo
            }
            categoriaProducto {
              id
              categoria
            }
            files {
              file
            }
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [cambiarPlato, { loading: loadingCambio }] = useMutation(
    gql`
      mutation cambiarPlato($id: ID!, $idDetalle: ID!, $idPlato: ID!, $turno: String) {
        cambiarPlato(id: $id, idDetalle: $idDetalle, idPlato: $idPlato, turno: $turno) {
          id
          detalles {
            id
            producto {
              id
              nombre
            }
            cantidad
            precio
          }
        }
      }
    `
  );

  useEffect(() => {
    if (open && pedido?.turnoReserva) {
      getProductos({
        variables: {
          filter: {
            turno: pedido.turnoReserva,
          },
        },
      });
    }
  }, [open, pedido?.turnoReserva]);

  useEffect(() => {
    if (data?.menusDelDia) {
      setMenusDisponibles(data.menusDelDia);
    }
  }, [data]);

  useEffect(() => {
    if (open) {
      setIdMenu("-1");
      setTurno(pedido?.turnoReserva || "");
    }
  }, [open]);

  const handleCambiarMenu = async () => {
    if (!idMenu) {
      handleSnackbar("Debe seleccionar un menú", "error");
      return;
    }

    const detalleActual = pedido?.detalles?.[0];
    if (!detalleActual) {
      handleSnackbar("No se encontró el detalle del pedido", "error");
      return;
    }

    try {
      await cambiarPlato({
        variables: {
          id: pedido.id,
          idDetalle: detalleActual.id,
          idPlato: idMenu,
          turno: turno || null,
        },
      });

      handleSnackbar("Menú cambiado exitosamente", "success");
      handleClose();
      refetch();
    } catch (error) {
      handleSnackbar(error.message || "Error al cambiar el menú", "error");
    }
  };

  // Siempre hay un solo detalle/menú por pedido
  const menuActual = pedido?.detalles?.[0];

  console.log("Pedido:", pedido);
  console.log("Menu actual:", menuActual);
  console.log("Menus disponibles:", menusDisponibles);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox>
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                pb={0}
              >
                <SoftBox>
                  <SoftTypography variant="h6">Cambiar menú del pedido</SoftTypography>
                </SoftBox>
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>

              <SoftBox px={3} pb={2}>
                {!menuActual ? (
                  <Alert severity="info">Este pedido no tiene menú para cambiar.</Alert>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <SoftTypography variant="h6" fontWeight="medium" color="text" mb={2}>
                        Menú actual:
                      </SoftTypography>
                      <SoftBox p={2}>
                        <SoftTypography variant="subtitle1" fontWeight="bold">
                          {menuActual.producto?.nombre}
                        </SoftTypography>
                        {/* <SoftTypography variant="body2" color="text.secondary">
                          Cantidad: {menuActual.cantidad}
                        </SoftTypography> */}
                      </SoftBox>
                    </Grid>

                    <Grid item xs={12}>
                      <InputLabel htmlFor="menu-select">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Seleccionar nuevo menú:
                        </SoftTypography>
                      </InputLabel>
                      <Select
                        fullWidth
                        value={idMenu || ""}
                        onChange={(e) => setIdMenu(e.target.value)}
                        disabled={loading}
                      >
                        <MenuItem value="-1" disabled>
                          Seleccione un menú
                        </MenuItem>
                        {menusDisponibles.map((menu) => (
                          <MenuItem key={menu.id} value={menu.producto.id}>
                            {menu.producto.nombre}
                            {menu.limiteReservas && (
                              <span style={{ color: "#666", fontSize: "0.8em" }}>
                                {" "}
                                ({menu.cantidadReservas}/{menu.limiteReservas} reservas)
                              </span>
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="turno-select">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Seleccionar turno:
                        </SoftTypography>
                      </InputLabel>
                      <Select
                        fullWidth
                        value={turno || ""}
                        onChange={(e) => setTurno(e.target.value)}
                        inputRef={inputRef}
                      >
                        <MenuItem value="" disabled>
                          Seleccione un turno
                        </MenuItem>
                        <MenuItem value="Mediodía">Mediodía</MenuItem>
                        <MenuItem value="Noche">Noche</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>
                )}
              </SoftBox>
            </SoftBox>

            {menuActual && (
              <SoftBox display="flex" justifyContent="end" alignItems="center" p={3} pt={0}>
                <SoftBox mr={1}>
                  <SoftButton variant="outlined" color="secondary" onClick={handleClose}>
                    Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox>
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    onClick={handleCambiarMenu}
                    disabled={!idMenu || loadingCambio || idMenu === "-1"}
                  >
                    <Icon sx={{ fontWeight: "light" }}>swap_horiz</Icon>
                    &nbsp; {loadingCambio ? "Cambiando..." : "Cambiar Menú"}
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCambiarMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  pedido: PropTypes.object,
};
